import { AnimatePresence } from 'framer-motion';
import { FC, useState } from 'react';
import styled from 'styled-components';

import Button from '@/components/Button';
import Text from '@/components/Text';
import useHasPermission from '@/hooks/useHasPermission';
import { FreelancerDepartmentDetailsResource } from '@/types/resources';

import {
  BoxHeadingBrake,
  BoxHeadingRow,
  EditButton,
  NoDetailsBox,
  NoDetailsText,
} from '..';
import FreelancerDepartment from './FreelancerDepartment';
import FreelancerDepartmentModal from './FreelancerDepartmentModal';
import FreelancerWages from './FreelancerWages';
import FreelancerWagesComment from './FreelancerWagesComment';

const AddButtonHolder = styled.div`
  text-align: right;
`;

const AddButton = styled(Button)`
  padding: 0 16px;
`;

const Holder = styled.div`
  display: flex;
  flex-direction: column;
  gap: 56px;
  margin-bottom: 56px;
`;

interface Props {
  departments?: FreelancerDepartmentDetailsResource[];
}

const FreelancerDepartments: FC<Props> = ({ departments }) => {
  const [showModal, setShowModal] = useState(false);
  const userCanSeeData = useHasPermission('view all freelancers');
  const userCanManageData = useHasPermission('manage freelancers');

  return (
    <>
      {!departments?.length ? (
        <>
          <BoxHeadingRow>
            <Text $color="gray" $type="smallHeadline">
              DEPARTMENT DETAILS
            </Text>
            <BoxHeadingBrake />
            {userCanManageData && (
              <EditButton
                small
                color="secondary"
                onClick={() => setShowModal(true)}
              >
                Add
              </EditButton>
            )}
          </BoxHeadingRow>
          <NoDetailsBox>
            <NoDetailsText $type="caption" $color="gray">
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M7.40625 6.66675H13.3322"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M7.40625 9.6297H13.3322"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M7.40625 12.5927H10.3692"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M13.023 18.5185H2.95949C2.76303 18.5185 2.57462 18.4369 2.43571 18.2917C2.29679 18.1464 2.21875 17.9495 2.21875 17.7441V2.25586C2.21875 2.05047 2.29679 1.8535 2.43571 1.70827C2.57462 1.56303 2.76303 1.48145 2.95949 1.48145H17.7743C17.9708 1.48145 18.1592 1.56303 18.2981 1.70827C18.437 1.8535 18.515 2.05047 18.515 2.25586V12.7768C18.515 12.8785 18.4959 12.9792 18.4587 13.0732C18.4214 13.1671 18.3669 13.2525 18.2981 13.3244L13.5468 18.2917C13.478 18.3636 13.3964 18.4206 13.3065 18.4595C13.2166 18.4985 13.1203 18.5185 13.023 18.5185V18.5185Z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M18.4463 12.5918H13.3281V17.7103"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              No details added
            </NoDetailsText>
          </NoDetailsBox>
        </>
      ) : (
        <Holder>
          {departments.map((department, index) => (
            <div key={department.id}>
              <FreelancerDepartment
                department={department}
                index={index + 1}
                departmentsCount={departments.length}
              />
              <FreelancerWages
                index={index + 1}
                departmentsCount={departments.length}
                wages={department.wages}
                departmentId={department.id}
              />
              {userCanSeeData && (
                <FreelancerWagesComment
                  comment={department.wages_comment}
                  departmentId={department.id}
                />
              )}
            </div>
          ))}
          {userCanManageData && (
            <AddButtonHolder>
              <AddButton onClick={() => setShowModal(true)} color="colorized">
                Add department
              </AddButton>
            </AddButtonHolder>
          )}
        </Holder>
      )}
      <AnimatePresence>
        {userCanManageData && showModal && (
          <FreelancerDepartmentModal onClose={() => setShowModal(false)} />
        )}
      </AnimatePresence>
    </>
  );
};

export default FreelancerDepartments;
